import { defineStore } from 'pinia';
import { ref } from 'vue';
import { captureEvent } from '../analytics';
import {
    ENABLED_LOCALES,
    DEFAULT_LOCALE,
    isLanguageSwitcherEnabled,
    resolveLocale,
    setI18nLocale,
    type LocaleCode,
} from '../i18n';

/**
 * App-wide UI preferences that aren't tied to identity or a single matter.
 *
 * Today this owns the UI language. There is no backend user-profile store, so
 * the preference is persisted to localStorage and restored on load — the same
 * pattern MatterSelection.vue uses for its view mode. If a server-side profile
 * arrives later, this store is the single place to add the round-trip.
 */
const LOCALE_STORAGE_KEY = 'prefs.locale';

export const usePreferenceStore = defineStore('preference', () => {
    // Restore the persisted locale (narrowed to a known code) before anything
    // renders. We deliberately do NOT auto-detect navigator.language yet —
    // English is the only shipped catalog — but resolveLocale() keeps that an
    // easy one-line change later.
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    const locale = ref<LocaleCode>(resolveLocale(stored));

    /** Push the restored locale into the i18n runtime at boot. */
    function initLocale(): void {
        setI18nLocale(locale.value);
    }

    /**
     * Change the active UI language: update the i18n runtime, persist the
     * choice, and record the (meaningful, persisted) user action. No-ops if
     * the locale is unchanged or unknown.
     */
    function setLocale(next: string): void {
        const resolved = resolveLocale(next);
        if (resolved === locale.value) return;
        const previous = locale.value;

        locale.value = resolved;
        setI18nLocale(resolved);
        localStorage.setItem(LOCALE_STORAGE_KEY, resolved);

        // Emitted after the change commits. Locale codes are stable enums —
        // no PII (see analytics PII policy).
        captureEvent('language_changed', {
            locale: resolved,
            previous_locale: previous,
        });
    }

    return {
        locale,
        // Only the enabled locales are offered; the switcher hides itself when
        // English is the only one (see isLanguageSwitcherEnabled).
        availableLocales: ENABLED_LOCALES,
        languageSwitcherEnabled: isLanguageSwitcherEnabled,
        defaultLocale: DEFAULT_LOCALE,
        initLocale,
        setLocale,
    };
});
